import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { monthRange, downloadCsv, downloadPdf, toastExportNeedsLoad } from '../reportUtils';
import { useTenantCompanyName, ReportToolbar, ReportDocument, fmtRp } from '../ReportShell';

export default function ReportPl() {
  const company = useTenantCompanyName();
  const { start: mStart, end: mEnd } = monthRange();
  const [plStart, setPlStart] = useState(mStart);
  const [plEnd, setPlEnd] = useState(mEnd);
  const [pl, setPl] = useState(null);

  useEffect(() => {
    setPl(null);
  }, [plStart, plEnd]);

  const loadPl = () => {
    api.get('/tenant/accounting/reports/pl', { params: { start: plStart, end: plEnd } }).then((r) => setPl(r.data)).catch(() => toast.error('Gagal L/R'));
  };

  return (
    <div>
      <ReportToolbar>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Dari</Label>
          <Input type="date" value={plStart} onChange={(e) => setPlStart(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Sampai</Label>
          <Input type="date" value={plEnd} onChange={(e) => setPlEnd(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <Button type="button" onClick={loadPl} className="h-9">
          Muat data
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!pl) return toastExportNeedsLoad();
            downloadCsv('/tenant/accounting/reports/pl', { start: plStart, end: plEnd }, 'laba_rugi.csv');
          }}
        >
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!pl) return toastExportNeedsLoad();
            downloadPdf('/tenant/accounting/reports/pl', { start: plStart, end: plEnd }, 'laba_rugi.pdf');
          }}
        >
          PDF
        </Button>
      </ReportToolbar>

      {pl && (
        <ReportDocument
          companyName={company}
          title="LAPORAN LABA RUGI"
          subtitle="Ringkasan pendapatan dan beban untuk periode berjalan"
          periodLabel={`Untuk periode ${plStart} s.d. ${plEnd}`}
        >
          <div className="acct-rpt-section">
            <p className="acct-rpt-section-label">Pendapatan</p>
            <table className="acct-rpt-pl-table">
              <tbody>
                {(pl.revenue_lines?.length ? pl.revenue_lines : [{ code: '—', name: '(Tidak ada transaksi)', amount: 0 }]).map((x, i) => (
                  <tr key={`${x.code}-rev-${i}`}>
                    <td className="acct-rpt-pl-indent">
                      {x.code} — {x.name}
                    </td>
                    <td className="acct-rpt-pl-num">{fmtRp(x.amount)}</td>
                  </tr>
                ))}
                <tr className="acct-rpt-pl-total">
                  <td>Jumlah pendapatan</td>
                  <td className="acct-rpt-pl-num">{fmtRp(pl.revenue_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="acct-rpt-section">
            <p className="acct-rpt-section-label">Beban</p>
            <table className="acct-rpt-pl-table">
              <tbody>
                {(pl.expense_lines?.length ? pl.expense_lines : [{ code: '—', name: '(Tidak ada transaksi)', amount: 0 }]).map((x, i) => (
                  <tr key={`${x.code}-exp-${i}`}>
                    <td className="acct-rpt-pl-indent">
                      {x.code} — {x.name}
                    </td>
                    <td className="acct-rpt-pl-num">{fmtRp(x.amount)}</td>
                  </tr>
                ))}
                <tr className="acct-rpt-pl-total">
                  <td>Jumlah beban</td>
                  <td className="acct-rpt-pl-num">{fmtRp(pl.expense_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <table className="acct-rpt-pl-table mt-6">
            <tbody>
              <tr className="acct-rpt-pl-grand">
                <td>Laba (rugi) bersih sebelum pajak</td>
                <td className="acct-rpt-pl-num">{fmtRp(pl.net_income)}</td>
              </tr>
            </tbody>
          </table>

          <p className="acct-rpt-summary-bar">
            <strong>IKHTISAR:</strong> Pendapatan {fmtRp(pl.revenue_total)} · Beban {fmtRp(pl.expense_total)} ·{' '}
            <strong>Laba bersih {fmtRp(pl.net_income)}</strong>
          </p>
        </ReportDocument>
      )}

      {!pl && <div className="acct-rpt-empty">Pilih periode lalu klik <strong>Muat data</strong>.</div>}
    </div>
  );
}
